/**
 * ProcessingPage — Full-screen generation status driven by backend phases.
 *
 * Polls the assembly's generation_status every 2s. When ready, auto-redirects
 * to the assembly review page. On failure, shows error with retry option.
 */

import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Sparkles, AlertCircle, ArrowLeft } from 'lucide-react'
import apiClient from '../services/api'
import { generationPhaseLabel, isGenerationInProgress } from '../utils/generationStatus'

export default function ProcessingPage() {
  const { assemblyId } = useParams<{ assemblyId: string }>()
  const navigate = useNavigate()

  const [generationStatus, setGenerationStatus] = useState<string>('pending')
  const [failed, setFailed] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Poll generation_status every 2s
  useEffect(() => {
    if (!assemblyId) return

    const poll = async () => {
      try {
        const res = await apiClient.get(`/assemblies/${assemblyId}`)
        const status = res.data.generation_status
        setGenerationStatus(status)

        // 'degraded' is a finished draft, produced through a fallback or
        // non-AI path. Treat it as done here: it is complete, and polling on
        // it would leave the editor watching a spinner forever. The reduced
        // quality is surfaced on the draft itself, not by withholding it.
        if (status === 'ready' || status === 'degraded') {
          if (pollRef.current) clearInterval(pollRef.current)
          navigate(`/assemblies/${assemblyId}`, { replace: true })
        } else if (status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current)
          setFailed(true)
          setErrorMsg(res.data.generation_error || 'Draft generation failed')
        }
      } catch {
        // Network error — keep polling
      }
    }

    poll() // immediate first check
    pollRef.current = setInterval(poll, 2000)

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [assemblyId, navigate])

  if (failed) {
    return (
      <div className="rq-page flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--rq-text)] mb-3">
            Generation Failed
          </h1>
          <p className="text-sm text-[var(--rq-text-soft)] mb-6 leading-relaxed">
            {errorMsg}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/assemblies')}
              className="rq-btn-ghost px-4 py-2 text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Assemblies
            </button>
            <button
              onClick={() => navigate('/create-ai-draft')}
              className="rq-btn-primary px-4 py-2 text-sm"
            >
              <Sparkles className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rq-page flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-2xl bg-[var(--rq-amber)]/10 flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-8 h-8 text-[var(--rq-amber)] animate-pulse" />
        </div>
        <h1 className="text-2xl font-bold text-[var(--rq-text)] mb-3">
          Generating your draft...
        </h1>
        <p className="text-sm text-[var(--rq-text-soft)] mb-8">
          This may take a moment depending on the scope of your footage.
        </p>

        <div className="h-6 flex items-center justify-center">
          <p className="text-sm text-[var(--rq-text-soft)]">
            {generationPhaseLabel(generationStatus)}
          </p>
        </div>

        <div className="mt-6 text-xs text-[var(--rq-text-soft)]">
          {isGenerationInProgress(generationStatus) ? 'Processing current scope' : ''}
        </div>
      </div>
    </div>
  )
}
